import { uniq } from 'lodash';

import {
  DEFAULT_ADVANCED_OPTIONS,
  DEFAULT_AGE_RANGE,
  DEFAULT_OPTIMIZATION,
  isDefaultAllowedWorkspace,
  USA,
} from './constants';
import {
  AdvancedOptions,
  FormMode,
  InheritedParams,
  MixAndMatchSelection,
  OptimizationSettings,
  ParentReport,
  ProductSelection,
  ReportKind,
  ReportRequestState,
} from './types';

export const createInitialState = (
  mode: FormMode = 'request',
): ReportRequestState => ({
  mode,
  // Market survey is by far the most common request; the type can still be changed.
  reportKind: mode === 'update' ? 'update_products' : 'market_survey',
  projectName: '',
  clientName: '',
  workspace: null,
  country: null,
  clts: [],
  includeFemale: 0.5,
  ageRanges: [{ ...DEFAULT_AGE_RANGE }],
  smokingHabits: 'All',
  sansRejector: true,
  ethnicities: { asian: true, black: true, hispanic: true, white: true },
  products: [],
  allowedWorkspaces: [],
  categories: [],
  customClassDbName: '',
  customClassDbProducts: [],
  parent: null,
  inherited: null,
  optimization: { ...DEFAULT_OPTIMIZATION },
  mixAndMatch: [],
  advanced: { ...DEFAULT_ADVANCED_OPTIONS },
  updateStrategy: 'version_controled_translation',
  reportIdToInherit: '',
  instanceSize: 'auto',
  jobDefinitionOverride: null,
  updateSource: null,
});

type SettableKey = Exclude<
  keyof ReportRequestState,
  'mode' | 'optimization' | 'advanced'
>;

export type ReportRequestAction =
  | {
      type: 'set';
      key: SettableKey;
      value: ReportRequestState[SettableKey];
    }
  | { type: 'selectReportKind'; kind: ReportKind }
  | { type: 'setCountry'; country: string | null }
  | { type: 'addProducts'; products: ProductSelection[] }
  | { type: 'removeProduct'; name: string }
  | { type: 'clearProducts' }
  | { type: 'toggleAllowedWorkspace'; workspace: string }
  | { type: 'toggleCategory'; category: string }
  | { type: 'selectParent'; parent: ParentReport | null }
  | { type: 'setInherited'; inherited: InheritedParams | null }
  | {
      type: 'restoreParent';
      parent: ParentReport;
      optimization: OptimizationSettings;
      mixAndMatch: MixAndMatchSelection[];
    }
  | { type: 'patchOptimization'; patch: Partial<OptimizationSettings> }
  | { type: 'patchAdvanced'; patch: Partial<AdvancedOptions> }
  | { type: 'replace'; state: ReportRequestState };

/** Typed helper so callers keep key/value pairs in sync. */
export const setField = <K extends SettableKey>(
  key: K,
  value: ReportRequestState[K],
): ReportRequestAction => ({ type: 'set', key, value });

/** Texture flags depend on one another; keep them coherent. */
const normaliseAdvanced = (advanced: AdvancedOptions): AdvancedOptions => {
  const next = { ...advanced };
  if (!next.include_texture) {
    next.texture_data_only = false;
    next.texture_only = false;
  }
  if (next.texture_data_only) next.texture_only = true;
  return next;
};

const productCategories = (products: ProductSelection[]) =>
  uniq(products.map((p) => p.category).filter((c): c is string => !!c));

const productWorkspaces = (products: ProductSelection[]) =>
  uniq(products.map((p) => p.workspace));

/**
 * A product's category across its workspace rows: the most common one, ties to
 * the earliest row. Some workspaces mislabel a product (e.g. one of Red Bull's 18
 * says "soda"), and auto-adding every row's category would skew the report.
 */
export const primaryCategory = (rows: ProductSelection[]): string | null => {
  const counts = new Map<string, number>();
  rows.forEach(
    (r) =>
      r.category && counts.set(r.category, (counts.get(r.category) ?? 0) + 1),
  );
  let best: string | null = null;
  counts.forEach((n, c) => {
    if (best === null || n > (counts.get(best) ?? 0)) best = c;
  });
  return best;
};

const addProducts = (
  state: ReportRequestState,
  products: ProductSelection[],
): ReportRequestState => {
  // A product is chosen by name, as a whole: rows for a name already in the set
  // (e.g. other workspaces of a templated or original product) are ignored, so
  // re-picking it can't quietly allow workspaces or add categories.
  const knownNames = new Set(state.products.map((p) => p.name));
  const incoming = products.filter((p) => !knownNames.has(p.name));
  if (incoming.length === 0) return state;
  const existingWorkspaces = productWorkspaces(state.products);
  const existingCategories = productCategories(state.products);
  const newWorkspaces = productWorkspaces(incoming).filter(
    (w) =>
      !existingWorkspaces.includes(w) &&
      isDefaultAllowedWorkspace(w, state.workspace?.name),
  );
  const allowedWorkspaces = uniq([
    ...state.allowedWorkspaces,
    ...newWorkspaces,
  ]);
  // Categories come from the rows the report may use; other clients' labels only
  // count when none of a product's workspaces are allowed.
  const incomingCategories = uniq(incoming.map((p) => p.name))
    .map((name) => {
      const rows = incoming.filter((p) => p.name === name);
      const usable = rows.filter((p) =>
        allowedWorkspaces.includes(p.workspace),
      );
      return primaryCategory(usable.length ? usable : rows);
    })
    .filter((c): c is string => !!c);
  return {
    ...state,
    products: [...state.products, ...incoming],
    allowedWorkspaces,
    categories: uniq([
      ...state.categories,
      ...incomingCategories.filter((c) => !existingCategories.includes(c)),
    ]),
  };
};

export const originalCategoriesOf = (state: ReportRequestState): string[] =>
  state.mode === 'update' ? state.updateSource?.originalCategories ?? [] : [];

/**
 * Original products of an update are locked; their workspaces and categories stay.
 * Only categories the removed product brought in are pruned, so categories that came
 * from a template (and may not match any catalog row) survive unrelated removals.
 */
const removeProduct = (
  state: ReportRequestState,
  name: string,
): ReportRequestState => {
  const removed = state.products.filter((p) => p.name === name && !p.original);
  const products = state.products.filter((p) => p.name !== name || p.original);
  const workspaces = productWorkspaces(products);
  const remainingCategories = productCategories(products);
  const removedCategories = productCategories(removed);
  const originalWorkspaces =
    state.mode === 'update' ? state.updateSource?.originalWorkspaces ?? [] : [];
  const originalCategories = originalCategoriesOf(state);
  return {
    ...state,
    products,
    allowedWorkspaces: state.allowedWorkspaces.filter(
      (w) => workspaces.includes(w) || originalWorkspaces.includes(w),
    ),
    categories: state.categories.filter(
      (c) =>
        !removedCategories.includes(c) ||
        remainingCategories.includes(c) ||
        originalCategories.includes(c),
    ),
  };
};

const toggle = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const patchOptimization = (
  state: ReportRequestState,
  patch: Partial<OptimizationSettings>,
): ReportRequestState => {
  const targetChanged =
    patch.targetProduct !== undefined &&
    patch.targetProduct !== state.optimization.targetProduct;
  return {
    ...state,
    optimization: { ...state.optimization, ...patch },
    // Mix & match versions belong to a specific target product.
    mixAndMatch:
      targetChanged && state.reportKind === 'mix_and_match'
        ? []
        : state.mixAndMatch,
  };
};

export const reportRequestReducer = (
  state: ReportRequestState,
  action: ReportRequestAction,
): ReportRequestState => {
  switch (action.type) {
    case 'set':
      return { ...state, [action.key]: action.value };

    case 'selectReportKind':
      if (state.reportKind === action.kind) return state;
      return {
        ...state,
        reportKind: action.kind,
        parent: null,
        inherited: null,
        optimization: { ...DEFAULT_OPTIMIZATION },
        mixAndMatch: [],
      };

    case 'setCountry':
      return {
        ...state,
        country: action.country,
        clts: [],
        ethnicities:
          action.country === USA
            ? state.ethnicities
            : { asian: true, black: true, hispanic: true, white: true },
      };

    case 'addProducts':
      return addProducts(state, action.products);

    case 'removeProduct':
      return removeProduct(state, action.name);

    case 'clearProducts':
      return { ...state, products: [], allowedWorkspaces: [], categories: [] };

    case 'toggleAllowedWorkspace':
      return {
        ...state,
        allowedWorkspaces: toggle(state.allowedWorkspaces, action.workspace),
      };

    case 'toggleCategory':
      if (originalCategoriesOf(state).includes(action.category)) return state;
      return {
        ...state,
        categories: toggle(state.categories, action.category),
      };

    case 'selectParent':
      if (state.parent?.reportId === action.parent?.reportId) return state;
      return {
        ...state,
        parent: action.parent,
        inherited: null,
        optimization: { ...DEFAULT_OPTIMIZATION },
        mixAndMatch: [],
      };

    case 'restoreParent':
      return {
        ...state,
        parent: action.parent,
        inherited: null,
        optimization: action.optimization,
        mixAndMatch: action.mixAndMatch,
      };

    case 'setInherited':
      return { ...state, inherited: action.inherited };

    case 'patchOptimization':
      return patchOptimization(state, action.patch);

    case 'patchAdvanced':
      return {
        ...state,
        advanced: normaliseAdvanced({ ...state.advanced, ...action.patch }),
      };

    case 'replace':
      return action.state;

    default:
      return state;
  }
};
